/**
 * What a caught value can be asked, without asserting what it is.
 *
 * A catch binding is `unknown`, and rightly so: anything can be thrown. The
 * adapters here needed four of its fields for a Sentry breadcrumb and read
 * them through `(error as any).name` and friends, which turns off checking for
 * the whole expression rather than for the one property being read.
 *
 * Every accessor returns undefined rather than throwing, because the point of
 * this file is to be safe to call from inside a catch block.
 *
 * The sister of apps/api/src/app/core/domain/what-an-error-says.ts, which does
 * the same job on the other side.
 */
type ErrorLike = { name?: unknown; message?: unknown; code?: unknown; stack?: unknown };

const asObject = (error: unknown): ErrorLike | undefined =>
  error !== null && typeof error === 'object' ? (error as ErrorLike) : undefined;

const stringField = (error: unknown, field: keyof ErrorLike): string | undefined => {
  const value = asObject(error)?.[field];
  return typeof value === 'string' && value ? value : undefined;
};

export const theErrorName = (error: unknown): string | undefined => stringField(error, 'name');
export const theErrorMessage = (error: unknown): string | undefined => stringField(error, 'message');
export const theErrorStack = (error: unknown): string | undefined => stringField(error, 'stack');

/**
 * A code is a string on a Firebase error and a number on some native plugins,
 * so it is returned as it was found rather than coerced.
 */
export const theErrorCode = (error: unknown): string | number | undefined => {
  const value = asObject(error)?.code;
  return typeof value === 'string' || typeof value === 'number' ? value : undefined;
};
